import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { List, Plus, Map, Home as HomeIcon, ArrowLeft, FileText, BarChart2, Wrench, Shield, Calendar, LogOut, MapPin, Layers, Lightbulb, Users, Settings } from 'lucide-react';

// Importações de módulos usando os arquivos de índice
import { AuthProvider, useAuth, ProtectedRoute, SessionManager, Home, Auth } from './modules/shared';
import ProtectedModule from './modules/shared/components/ProtectedModule';
import { ObrasProvider, ListaObras, CadastroObra, MapaObras, GerenciadorMedicoes, DashboardEstatisticas as ObrasDashboard } from './modules/obras';
import { ObrasFixasProvider, ListaObrasFixas, CadastroObraFixa, MapaObrasFixas, GerenciadorMedicoes as GerenciadorMedicoesFixas, DashboardEstatisticas as ObrasFixasDashboard } from './modules/obras-fixas';
import { MapaIntegrado, MapaPublicoWrapper } from './modules/mapa-integrado';
import { 
  FossasSepticasProvider, 
  DashboardEstatisticas,
  ListaPropriedadesRurais,
  CadastroPropriedadeRural,
  ListaResidencias,
  MapaPropriedadesRurais,
  GerenciadorAgentes,
  CadastroLocalidade,
  HistoricoVisitas,
  ListaFossasSepticas,
  CadastroFossaSeptica,
  CadastroFossaSepticaWrapper,
  DistribuicaoPropriedades
} from './modules/fossas-septicas';
import {
  PostesIluminacaoProvider,
  DashboardEstatisticas as PostesDashboard,
  ListaPostes,
  CadastroPoste,
  MapaPostes,
  DetalhesPoste
} from './modules/postes-iluminacao';
import './modules/postes-iluminacao/styles.css';
import {
  GestaoUsuariosProvider,
  DashboardUsuarios,
  ListaUsuarios,
  CadastroUsuario,
  DetalhesUsuario,
  ListaColaboradores,
  CadastroColaborador,
  ResetPassword,
  DashboardPopulacao,
  DetalhesUsuarioAplicativo,
  DetalhesUsuarioWhatsapp
} from './modules/gestao-usuarios';
import './modules/gestao-usuarios/styles.css';
import {
  ControleDemandaProvider,
  DashboardDemandas,
  ListaDemandas,
  ListaAnalises
} from './modules/controle-demandas';
import './modules/controle-demandas/styles.css';



// Configuração dos módulos do sistema
const systemModules = {
  obras: {
    name: 'Sistema de Obras em Vias Públicas',
    icon: <Map size={20} />,
    color: '#667eea'
  },
  'obras-fixas': {
    name: 'Sistema de Obras Fixas',
    icon: <MapPin size={20} />,
    color: '#9c27b0'
  },
  'mapa-integrado': {
    name: 'Mapa Integrado',
    icon: <Layers size={20} />,
    color: '#3f51b5'
  },
  'fossas-septicas': {
    name: 'Administração de Fossas Sépticas',
    icon: <Shield size={20} />,
    color: '#2e7d32'
  },
  'postes-iluminacao': {
    name: 'Postes de Iluminação Pública',
    icon: <Lightbulb size={20} />,
    color: '#ff6f00'
  },
  'gestao-usuarios': {
    name: 'Gestão de Usuários',
    icon: <Users size={20} />,
    color: '#6c757d'
  },
  'controle-demandas': {
    name: 'Controle de Demandas',
    icon: <FileText size={20} />,
    color: '#e74c3c'
  },
  contratos: {
    name: 'Contratos e Licitações',
    icon: <FileText size={20} />,
    color: '#43a047'
  },
  relatorios: {
    name: 'Relatórios e Dashboards',
    icon: <BarChart2 size={20} />,
    color: '#ff9800'
  },
  manutencao: {
    name: 'Manutenção de Vias',
    icon: <Wrench size={20} />,
    color: '#e53935'
  },
  fiscalizacao: {
    name: 'Fiscalização e Vistorias',
    icon: <Shield size={20} />,
    color: '#1e88e5'
  },
  agenda: {
    name: 'Agenda e Cronogramas',
    icon: <Calendar size={20} />,
    color: '#8e24aa'
  }
};

function Navigation() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isMapPage = location.pathname === '/obras/mapa' || location.pathname === '/obras-fixas/mapa' || location.pathname === '/mapa-integrado' || location.pathname === '/postes-iluminacao/mapa';


  const isHomePage = location.pathname === '/';

  // Não mostrar navegação em páginas de autenticação
  if (location.pathname === '/login') return null;

  // Identificar o módulo atual
  // Ordenar as chaves por comprimento (decrescente) para garantir que 'obras-fixas' seja verificado antes de 'obras'
  const sortedModuleKeys = Object.keys(systemModules).sort((a, b) => b.length - a.length);
  const currentModule = sortedModuleKeys.find(key =>
    location.pathname.startsWith(`/${key}`)
  );

  // Não mostrar navegação na página inicial
  if (isHomePage) return null;

  const moduleInfo = currentModule ? systemModules[currentModule] : null;

  return (
    <div className={isMapPage ? "header-compact" : "header"} style={{
      background: moduleInfo ? `linear-gradient(135deg, ${moduleInfo.color} 0%, ${adjustColor(moduleInfo.color, -30)} 100%)` : undefined
    }}>
      <div className="container">
        {!isMapPage && moduleInfo && (
          <>
            <div className="header-content">
              <h1>{moduleInfo.name}</h1>
              <p>Prefeitura Teste</p>
            </div>
          </>
        )}
        <nav className={isMapPage ? "nav-compact" : "nav"}>
          <Link
            to="/"
            className="nav-button"
          >
            <HomeIcon size={16} />
            {!isMapPage && 'Início'}
          </Link>

          {currentModule === 'obras' && (
            <>
              <Link
                to="/obras/dashboard"
                className={`nav-button ${location.pathname === '/obras/dashboard' ? 'active' : ''}`}
              >
                <BarChart2 size={16} />
                {!isMapPage && 'Dashboard'}
              </Link>
              <Link
                to="/obras"
                className={`nav-button ${location.pathname === '/obras' ? 'active' : ''}`}
              >
                <List size={16} />
                {!isMapPage && 'Lista de Obras'}
              </Link>
              <Link
                to="/obras/cadastro"
                className={`nav-button ${location.pathname === '/obras/cadastro' ? 'active' : ''}`}
              >
                <Plus size={16} />
                {!isMapPage && 'Nova Obra'}
              </Link>
              <Link
                to="/obras/mapa"
                className={`nav-button ${location.pathname === '/obras/mapa' ? 'active' : ''}`}
              >
                <Map size={16} />
                {!isMapPage && 'Mapa das Obras'}
              </Link>
            </>
          )}

          {currentModule === 'obras-fixas' && (
            <>
              <Link
                to="/obras-fixas/dashboard"
                className={`nav-button ${location.pathname === '/obras-fixas/dashboard' ? 'active' : ''}`}
              >
                <BarChart2 size={16} />
                {!isMapPage && 'Dashboard'}
              </Link>
              <Link
                to="/obras-fixas"
                className={`nav-button ${location.pathname === '/obras-fixas' ? 'active' : ''}`}
              >
                <List size={16} />
                {!isMapPage && 'Lista de Obras Fixas'}
              </Link>
              <Link
                to="/obras-fixas/cadastro"
                className={`nav-button ${location.pathname === '/obras-fixas/cadastro' ? 'active' : ''}`}
              >
                <Plus size={16} />
                {!isMapPage && 'Nova Obra Fixa'}
              </Link>
              <Link
                to="/obras-fixas/mapa"
                className={`nav-button ${location.pathname === '/obras-fixas/mapa' ? 'active' : ''}`}
              >
                <MapPin size={16} />
                {!isMapPage && 'Mapa das Obras Fixas'}
              </Link>
            </>
          )}

          {currentModule === 'fossas-septicas' && (
            <>
              <Link
                to="/fossas-septicas/dashboard"
                className={`nav-button ${location.pathname === '/fossas-septicas/dashboard' ? 'active' : ''}`}
              >
                <BarChart2 size={16} />
                {!isMapPage && 'Dashboard'}
              </Link>
              <Link
                to="/fossas-septicas/propriedades"
                className={`nav-button ${location.pathname === '/fossas-septicas/propriedades' ? 'active' : ''}`}
              >
                <List size={16} />
                {!isMapPage && 'Propriedades'}
              </Link>
              <Link
                to="/fossas-septicas/cadastro"
                className={`nav-button ${location.pathname === '/fossas-septicas/cadastro' ? 'active' : ''}`}
              >
                <Plus size={16} />
                {!isMapPage && 'Nova Propriedade'}
              </Link>
              <Link
                to="/fossas-septicas/mapa"
                className={`nav-button ${location.pathname === '/fossas-septicas/mapa' ? 'active' : ''}`}
              >
                <Map size={16} />
                {!isMapPage && 'Mapa'}
              </Link>
              <Link
                to="/fossas-septicas/agentes"
                className={`nav-button ${location.pathname === '/fossas-septicas/agentes' ? 'active' : ''}`}
              >
                <Shield size={16} />
                {!isMapPage && 'Agentes'}
              </Link>
              <Link
                to="/fossas-septicas/fossas"
                className={`nav-button ${location.pathname === '/fossas-septicas/fossas' ? 'active' : ''}`}
              >
                <MapPin size={16} />
                {!isMapPage && 'Fossas Sépticas'}
              </Link>
            </>
          )}

          {currentModule === 'postes-iluminacao' && (
            <>
              <Link
                to="/postes-iluminacao/dashboard"
                className={`nav-button ${location.pathname === '/postes-iluminacao/dashboard' ? 'active' : ''}`}
              >
                <BarChart2 size={16} />
                {!isMapPage && 'Dashboard'}
              </Link>
              <Link
                to="/postes-iluminacao"
                className={`nav-button ${location.pathname === '/postes-iluminacao' ? 'active' : ''}`}
              >
                <List size={16} />
                {!isMapPage && 'Lista de Postes'}
              </Link>
              <Link
                to="/postes-iluminacao/cadastro"
                className={`nav-button ${location.pathname === '/postes-iluminacao/cadastro' ? 'active' : ''}`}
              >
                <Plus size={16} />
                {!isMapPage && 'Novo Poste'}
              </Link>
              <Link
                to="/postes-iluminacao/mapa"
                className={`nav-button ${location.pathname === '/postes-iluminacao/mapa' ? 'active' : ''}`}
              >
                <Map size={16} />
                {!isMapPage && 'Mapa'}
              </Link>
            </>
          )}

          

          {currentModule === 'controle-demandas' && (
            <>
              <Link
                to="/controle-demandas"
                className={`nav-button ${location.pathname === '/controle-demandas' ? 'active' : ''}`}
              >
                <BarChart2 size={16} />
                {!isMapPage && 'Dashboard'}
              </Link>
              <Link
                to="/controle-demandas/demandas"
                className={`nav-button ${location.pathname === '/controle-demandas/demandas' ? 'active' : ''}`}
              >
                <FileText size={16} />
                {!isMapPage && 'Demandas'}
              </Link>
              <Link
                to="/controle-demandas/analises"
                className={`nav-button ${location.pathname === '/controle-demandas/analises' ? 'active' : ''}`}
              >
                <Shield size={16} />
                {!isMapPage && 'Análises'}
              </Link>

            </>
          )}

          {currentModule === 'gestao-usuarios' && (
            <>
              <Link
                to="/gestao-usuarios/dashboard"
                className={`nav-button ${location.pathname === '/gestao-usuarios/dashboard' ? 'active' : ''}`}
              >
                <BarChart2 size={16} />
                {!isMapPage && 'Dashboard'}
              </Link>
              <Link
                to="/gestao-usuarios"
                className={`nav-button ${location.pathname === '/gestao-usuarios' ? 'active' : ''}`}
              >
                <List size={16} />
                {!isMapPage && 'Usuários Sistema'}
              </Link>
              <Link
                to="/gestao-usuarios/colaboradores"
                className={`nav-button ${location.pathname.includes('/gestao-usuarios/colaboradores') ? 'active' : ''}`}
              >
                <Users size={16} />
                {!isMapPage && 'Colaboradores'}
              </Link>
              <Link
                to="/gestao-usuarios/populacao"
                className={`nav-button ${location.pathname.includes('/gestao-usuarios/populacao') ? 'active' : ''}`}
              >
                <Users size={16} />
                {!isMapPage && 'População'}
              </Link>
              <Link
                to="/gestao-usuarios/cadastro"
                className={`nav-button ${location.pathname === '/gestao-usuarios/cadastro' ? 'active' : ''}`}
              >
                <Plus size={16} />
                {!isMapPage && 'Novo Usuário'}
              </Link>
            </>
          )}

          {user && (
            <button
              onClick={logout}
              className="nav-button"
              style={{ marginLeft: 'auto' }}
            >
              <LogOut size={16} />
              {!isMapPage && 'Sair'}
            </button>
          )}
        </nav>
      </div>
    </div>
  );
}

// Função auxiliar para ajustar a cor (escurecer/clarear)
function adjustColor(color, amount) {
  // Converte cor hex para RGB
  let hex = color.replace('#', '');
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Ajusta os valores
  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));

  // Converte de volta para hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function AppContent() {
  const location = useLocation();
  const isMapPage = location.pathname === '/obras/mapa' || location.pathname === '/obras-fixas/mapa' || location.pathname === '/mapa-integrado' || location.pathname === '/postes-iluminacao/mapa';
  const isPublicPage = location.pathname === '/mapa-publico';

  return (
    <div className="App" style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Componente para gerenciar a sessão entre abas */}
      <SessionManager />

      {/* Não mostrar navegação em páginas públicas */}
      {!isPublicPage && <Navigation />}
      <div className={isMapPage || isPublicPage ? "map-container-fullscreen" : "container"} style={{
        flex: 1,
        overflow: isMapPage || isPublicPage ? 'hidden' : 'auto'
      }}>
        <Routes>
          {/* Rotas públicas */}
          <Route path="/login" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Rotas protegidas */}
          {/* Rota pública para o mapa integrado */}
          <Route path="/mapa-publico" element={<MapaPublicoWrapper />} />
          
          <Route element={<ProtectedRoute />}>
            {/* Página inicial com módulos */}
            <Route path="/" element={<Home />} />

            {/* Módulo de Obras em Vias Públicas */}
            <Route path="/obras" element={<ProtectedModule module="obras"><ListaObras /></ProtectedModule>} />
            <Route path="/obras/dashboard" element={<ProtectedModule module="obras"><ObrasDashboard /></ProtectedModule>} />
            <Route path="/obras/cadastro" element={<ProtectedModule module="obras"><CadastroObra /></ProtectedModule>} />
            <Route path="/obras/editar/:id" element={<ProtectedModule module="obras"><CadastroObra /></ProtectedModule>} />
            <Route path="/obras/medicoes/:id" element={<ProtectedModule module="obras"><GerenciadorMedicoes /></ProtectedModule>} />
            <Route path="/obras/mapa" element={<ProtectedModule module="obras"><MapaObras /></ProtectedModule>} />

            {/* Módulo de Obras Fixas */}
            <Route path="/obras-fixas" element={<ProtectedModule module="obras-fixas"><ListaObrasFixas /></ProtectedModule>} />
            <Route path="/obras-fixas/dashboard" element={<ProtectedModule module="obras-fixas"><ObrasFixasDashboard /></ProtectedModule>} />
            <Route path="/obras-fixas/cadastro" element={<ProtectedModule module="obras-fixas"><CadastroObraFixa /></ProtectedModule>} />
            <Route path="/obras-fixas/editar/:id" element={<ProtectedModule module="obras-fixas"><CadastroObraFixa /></ProtectedModule>} />
            <Route path="/obras-fixas/medicoes/:id" element={<ProtectedModule module="obras-fixas"><GerenciadorMedicoesFixas /></ProtectedModule>} />
            <Route path="/obras-fixas/mapa" element={<ProtectedModule module="obras-fixas"><MapaObrasFixas /></ProtectedModule>} />

            {/* Módulo de Mapa Integrado */}
            <Route path="/mapa-integrado" element={<ProtectedModule module="mapa-integrado"><MapaIntegrado /></ProtectedModule>} />

            {/* Módulo de Fossas Sépticas */}
            <Route path="/fossas-septicas/dashboard" element={<ProtectedModule module="fossas-septicas"><DashboardEstatisticas /></ProtectedModule>} />
            <Route path="/fossas-septicas/propriedades" element={<ProtectedModule module="fossas-septicas"><ListaPropriedadesRurais /></ProtectedModule>} />
            <Route path="/fossas-septicas/cadastro" element={<ProtectedModule module="fossas-septicas"><CadastroPropriedadeRural /></ProtectedModule>} />
            <Route path="/fossas-septicas/editar/:id" element={<ProtectedModule module="fossas-septicas"><CadastroPropriedadeRural /></ProtectedModule>} />
            <Route path="/fossas-septicas/mapa" element={<ProtectedModule module="fossas-septicas"><MapaPropriedadesRurais /></ProtectedModule>} />
            <Route path="/fossas-septicas/agentes" element={<ProtectedModule module="fossas-septicas"><GerenciadorAgentes /></ProtectedModule>} />
            <Route path="/fossas-septicas/localidades" element={<ProtectedModule module="fossas-septicas"><CadastroLocalidade /></ProtectedModule>} />
            <Route path="/fossas-septicas/distribuicao" element={<ProtectedModule module="fossas-septicas"><DistribuicaoPropriedades /></ProtectedModule>} />
            <Route path="/fossas-septicas/visitas/:id" element={<ProtectedModule module="fossas-septicas"><HistoricoVisitas /></ProtectedModule>} />
            <Route path="/fossas-septicas/fossas" element={<ProtectedModule module="fossas-septicas"><ListaFossasSepticas /></ProtectedModule>} />
            <Route path="/fossas-septicas/fossas/cadastro/:residenciaId" element={<ProtectedModule module="fossas-septicas"><CadastroFossaSepticaWrapper /></ProtectedModule>} />
            <Route path="/fossas-septicas/residencias/:id" element={<ProtectedModule module="fossas-septicas"><ListaResidencias /></ProtectedModule>} />

            {/* Módulo de Postes de Iluminação */}
            <Route path="/postes-iluminacao/dashboard" element={<ProtectedModule module="postes-iluminacao"><PostesDashboard /></ProtectedModule>} />
            <Route path="/postes-iluminacao" element={<ProtectedModule module="postes-iluminacao"><ListaPostes /></ProtectedModule>} />
            <Route path="/postes-iluminacao/cadastro" element={<ProtectedModule module="postes-iluminacao"><CadastroPoste /></ProtectedModule>} />
            <Route path="/postes-iluminacao/editar/:id" element={<ProtectedModule module="postes-iluminacao"><CadastroPoste /></ProtectedModule>} />
            <Route path="/postes-iluminacao/detalhes/:id" element={<ProtectedModule module="postes-iluminacao"><DetalhesPoste /></ProtectedModule>} />
            <Route path="/postes-iluminacao/mapa" element={<ProtectedModule module="postes-iluminacao"><MapaPostes /></ProtectedModule>} />

            {/* Módulo de Gestão de Usuários */}
            <Route path="/gestao-usuarios/dashboard" element={<ProtectedModule module="gestao-usuarios"><DashboardUsuarios /></ProtectedModule>} />
            <Route path="/gestao-usuarios" element={<ProtectedModule module="gestao-usuarios"><ListaUsuarios /></ProtectedModule>} />
            <Route path="/gestao-usuarios/cadastro" element={<ProtectedModule module="gestao-usuarios"><CadastroUsuario /></ProtectedModule>} />
            <Route path="/gestao-usuarios/editar/:id" element={<ProtectedModule module="gestao-usuarios"><CadastroUsuario /></ProtectedModule>} />
            <Route path="/gestao-usuarios/detalhes/:id" element={<ProtectedModule module="gestao-usuarios"><DetalhesUsuario /></ProtectedModule>} />
            
            {/* Gestão de Colaboradores */}
            <Route path="/gestao-usuarios/colaboradores" element={<ProtectedModule module="gestao-usuarios"><ListaColaboradores /></ProtectedModule>} />
            <Route path="/gestao-usuarios/colaboradores/cadastro" element={<ProtectedModule module="gestao-usuarios"><CadastroColaborador /></ProtectedModule>} />
            <Route path="/gestao-usuarios/colaboradores/editar/:id" element={<ProtectedModule module="gestao-usuarios"><CadastroColaborador /></ProtectedModule>} />
            <Route path="/gestao-usuarios/colaboradores/detalhes/:id" element={<ProtectedModule module="gestao-usuarios"><DetalhesUsuario /></ProtectedModule>} />

            {/* População de Usuários */}
            <Route path="/gestao-usuarios/populacao" element={<ProtectedModule module="gestao-usuarios"><DashboardPopulacao /></ProtectedModule>} />
            <Route path="/gestao-usuarios/populacao/aplicativo/detalhes/:id" element={<ProtectedModule module="gestao-usuarios"><DetalhesUsuarioAplicativo /></ProtectedModule>} />
            <Route path="/gestao-usuarios/populacao/whatsapp/detalhes/:id" element={<ProtectedModule module="gestao-usuarios"><DetalhesUsuarioWhatsapp /></ProtectedModule>} />

            {/* Módulo de Controle de Demandas */}
            <Route path="/controle-demandas" element={<ProtectedModule module="controle-demandas"><DashboardDemandas /></ProtectedModule>} />
            <Route path="/controle-demandas/demandas" element={<ProtectedModule module="controle-demandas"><ListaDemandas /></ProtectedModule>} />
            <Route path="/controle-demandas/analises" element={<ProtectedModule module="controle-demandas"><ListaAnalises /></ProtectedModule>} />


            {/* Redirecionamentos para rotas antigas */}
            <Route path="/cadastro" element={<Navigate to="/obras/cadastro" replace />} />
            <Route path="/editar/:id" element={<Navigate to="/obras/editar/:id" replace />} />
            <Route path="/medicoes/:id" element={<Navigate to="/obras/medicoes/:id" replace />} />
            <Route path="/mapa" element={<Navigate to="/obras/mapa" replace />} />

            {/* Outros módulos (em breve) */}
            <Route path="/contratos" element={<ModuleUnderConstruction name="Contratos e Licitações" color="#43a047" />} />
            <Route path="/relatorios" element={<ModuleUnderConstruction name="Relatórios e Dashboards" color="#ff9800" />} />
            <Route path="/manutencao" element={<ModuleUnderConstruction name="Manutenção de Vias" color="#e53935" />} />
            <Route path="/fiscalizacao" element={<ModuleUnderConstruction name="Fiscalização e Vistorias" color="#1e88e5" />} />
            <Route path="/agenda" element={<ModuleUnderConstruction name="Agenda e Cronogramas" color="#8e24aa" />} />
          </Route>

          {/* Rota de fallback */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
      <Toaster position="top-right" />
    </div>
  );
}

// Componente para módulos em construção
function ModuleUnderConstruction({ name, color = '#667eea' }) {
  return (
    <div className="module-under-construction">
      <div className="card" style={{ borderTop: `5px solid ${color}` }}>
        <h2 style={{ color }}>{name}</h2>
        <p>Este módulo está em desenvolvimento e estará disponível em breve.</p>
        <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center' }}>
          <Link to="/" className="button" style={{ backgroundColor: color }}>
            <ArrowLeft size={16} />
            Voltar para a página inicial
          </Link>
        </div>
      </div>
    </div>
  );
}

// Os componentes do módulo de fossas sépticas agora são importados do módulo

function App() {
  return (
    <Router>
      <AuthProvider>
        <ObrasProvider>
          <ObrasFixasProvider>
            <FossasSepticasProvider>
              <PostesIluminacaoProvider>
                <GestaoUsuariosProvider>
                  <ControleDemandaProvider>
                    <AppContent />
                  </ControleDemandaProvider>
                </GestaoUsuariosProvider>
              </PostesIluminacaoProvider>
            </FossasSepticasProvider>
          </ObrasFixasProvider>
        </ObrasProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;